import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

export const AdminNewsDisplay = () => {
  const { newId } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    fetchNewsDetails();
  }, [newId]);

  const fetchNewsDetails = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin-news/news/${newId}`,
        { withCredentials: true }
      );

      if (!response.data) {
        setError("News not found");
        setLoading(false);
        return;
      }

      setNews(response.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch news details");
      setLoading(false);
    }
  };

  if (loading) return <p style={styles.loading}>Loading news...</p>;
  if (error) return <p style={styles.error}>{error}</p>;

  return (
    <div style={styles.container} className="admin-news-display">
      <style>{`
        @media (max-width: 1024px) {
          .admin-news-display { padding: 16px; }
        }
        @media (max-width: 768px) {
          .admin-news-display { padding: 14px; }
          .admin-news-display .admin-display-header { flex-direction: column; align-items: flex-start; gap: 10px; }
          .admin-news-display .admin-display-buttons { width: 100%; display: flex; gap: 8px; }
          .admin-news-display .admin-display-buttons button { flex: 1; }
          .admin-news-display .admin-display-meta { flex-direction: column; align-items: flex-start; }
          .admin-news-display .admin-display-content { padding: 16px; }
          .admin-news-display .admin-display-images { gap: 8px; }
        }
        @media (max-width: 480px) {
          .admin-news-display { padding: 12px; }
          .admin-news-display .admin-display-content { padding: 14px; }
        }
      `}</style>
      {/* Header with buttons */}
      <div style={styles.header} className="admin-display-header">
        <h2 style={styles.title}>{news.title}</h2>
        <div style={styles.buttonContainer} className="admin-display-buttons">
          <button
            onClick={() => navigate("/DsportAdmin")}
            style={styles.backButton}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#5a6268")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#6c757d")}
          >
            Back to Admin
          </button>
          <button
            onClick={handleLogout}
            style={styles.logoutButton}
            onMouseOver={(e) => (e.target.style.backgroundColor = "#c0392b")}
            onMouseOut={(e) => (e.target.style.backgroundColor = "#e74c3c")}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Meta information */}
      <div style={styles.metaInfo} className="admin-display-meta">
        {news.sport_name && (
          <span style={styles.sportBadge}>🏆 {news.sport_name}</span>
        )}
        <span style={styles.clicksBadge}>👆 {news.clicks || 0} clicks</span>
        <span style={styles.dateBadge}>
          📅 {new Date(news.created_at).toLocaleDateString()}
        </span>
        <span style={styles.statusBadge}>
          {news.approved ? "✅ Published" : "⏳ Draft"}
        </span>
      </div>

      {/* Content with Markdown rendering */}
      <div style={styles.contentContainer} className="admin-display-content">
        <ReactMarkdown style={styles.markdown}>{news.content}</ReactMarkdown>
      </div>

      {news.references?.length > 0 && (
        <div style={styles.references}>
          <strong>References:</strong>
          <ul>
            {news.references.map((ref, index) => (
              <li key={index}>
                <a
                  href={ref}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.link}
                >
                  {ref}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {news.images?.length > 0 && (
        <div style={styles.imageContainer} className="admin-display-images">
          <strong>Images:</strong>
          <div style={styles.imageList}>
            {news.images.map((imgSrc, index) => (
              <img
                key={index}
                src={imgSrc}
                alt={`News ${index}`}
                style={styles.image}
              />
            ))}
          </div>
        </div>
      )}

      {news.video_path && (
        <div style={styles.videoContainer}>
          <strong>Video:</strong>
          <video controls style={styles.video}>
            <source
              src={`${process.env.REACT_APP_BASE_URL}${news.video_path}`}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
        </div>
      )}
    </div>
  );
};

// Inline CSS styles
const styles = {
  container: {
    padding: "20px",
    maxWidth: "900px",
    margin: "auto",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
    paddingBottom: "15px",
    borderBottom: "2px solid #eee",
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    margin: "0",
    color: "#2c3e50",
  },
  buttonContainer: {
    display: "flex",
    gap: "10px",
  },
  backButton: {
    backgroundColor: "#6c757d",
    color: "white",
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  logoutButton: {
    backgroundColor: "#e74c3c",
    color: "white",
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  metaInfo: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "25px",
    padding: "15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
  },
  sportBadge: {
    backgroundColor: "#e9ecef",
    color: "#495057",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "600",
  },
  clicksBadge: {
    backgroundColor: "#d1ecf1",
    color: "#0c5460",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "600",
  },
  dateBadge: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "600",
  },
  statusBadge: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "4px 12px",
    borderRadius: "16px",
    fontSize: "12px",
    fontWeight: "600",
  },
  contentContainer: {
    backgroundColor: "white",
    padding: "25px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "25px",
    lineHeight: "1.6",
  },
  markdown: {
    fontSize: "16px",
    color: "#333",
  },
  references: {
    marginTop: "10px",
    fontSize: "14px",
  },
  link: {
    color: "#007BFF",
    textDecoration: "underline",
  },
  imageContainer: {
    marginTop: "15px",
  },
  imageList: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
  },
  image: {
    width: "150px",
    height: "100px",
    objectFit: "cover",
    borderRadius: "8px",
  },
  videoContainer: {
    marginTop: "15px",
  },
  video: {
    width: "100%",
    maxWidth: "600px",
    borderRadius: "8px",
  },
  loading: {
    fontSize: "16px",
    textAlign: "center",
  },
  error: {
    fontSize: "16px",
    color: "red",
    textAlign: "center",
  },
};

export default AdminNewsDisplay;
